/**
 * useOverviewLiveFeed — REQ-090 state machine + live feed for the Admin
 * Operational Overview dashboard (ODE-P07-T01).
 *
 * Flow: token → `loading` → snapshot fetch (GET
 * /v1/admin/overview/snapshot):
 *   200 → `ready` (server-derived snapshot) + open the WebSocket live feed
 *   401 → `unauthorized`   (no socket is opened)
 *   403 → `blocked`        (no socket is opened — server-side authority)
 *   503/network → `error`  (no socket is opened)
 *
 * The feed status is tracked separately (`connecting` → `live` →
 * `disconnected`), so a dropped socket surfaces as the REQ-090 disconnected
 * state: the last snapshot stays visible but is never presented as live.
 * `reload()` re-runs the whole flow (fetch + fresh socket) — the explicit
 * reconnect loop with ODE-OD-008 retry semantics (503/network retryable;
 * 401/403 require re-authentication).
 *
 * Traces to: ODE-PRD-REQ-043, ODE-PRD-REQ-044, ODE-PRD-REQ-090.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { api, type OverviewFetchResult, type OverviewSnapshot } from '../../api/client.js';
import {
  createDefaultSocket,
  parseStreamFrame,
  type OverviewFeedStatus,
  type WebSocketLike,
} from './live-feed.js';

export type OverviewDataState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly snapshot: OverviewSnapshot }
  | { readonly kind: 'unauthorized' }
  | { readonly kind: 'blocked'; readonly message: string }
  | { readonly kind: 'error'; readonly message: string };

export interface UseOverviewLiveFeedOptions {
  readonly token: string | null;
  /** Test seam: socket factory (default: real browser WebSocket). */
  readonly createSocket?: ((url: string) => WebSocketLike | null) | undefined;
  /** Test seam: snapshot fetch (default: the shared api client). */
  readonly fetchSnapshot?: ((token: string) => Promise<OverviewFetchResult>) | undefined;
  /** Test seam: stream URL builder (default: the shared api client). */
  readonly streamUrl?: ((token: string) => string) | undefined;
}

export interface OverviewLiveFeedResult {
  readonly data: OverviewDataState;
  readonly feed: OverviewFeedStatus;
  readonly reload: () => void;
}

export function useOverviewLiveFeed(
  options: UseOverviewLiveFeedOptions,
): OverviewLiveFeedResult {
  const { token } = options;
  const [data, setData] = useState<OverviewDataState>({ kind: 'loading' });
  const [feed, setFeed] = useState<OverviewFeedStatus>('connecting');
  const [generation, setGeneration] = useState(0);

  // Seams live in refs so the effect depends only on [token, generation]:
  // a caller passing inline lambdas re-renders the hook without re-running
  // the fetch/socket lifecycle.
  const createSocketRef = useRef(options.createSocket ?? createDefaultSocket);
  createSocketRef.current = options.createSocket ?? createDefaultSocket;
  const fetchSnapshotRef = useRef(
    options.fetchSnapshot ?? ((tokenValue: string) => api.fetchOverviewSnapshot(tokenValue)),
  );
  fetchSnapshotRef.current =
    options.fetchSnapshot ?? ((tokenValue: string) => api.fetchOverviewSnapshot(tokenValue));
  const streamUrlRef = useRef(options.streamUrl ?? ((tokenValue: string) => api.overviewStreamUrl(tokenValue)));
  streamUrlRef.current =
    options.streamUrl ?? ((tokenValue: string) => api.overviewStreamUrl(tokenValue));

  const reload = useCallback(() => {
    setGeneration((current) => current + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    let socket: WebSocketLike | null = null;
    setData({ kind: 'loading' });
    setFeed('connecting');

    if (token === null) {
      setData({ kind: 'unauthorized' });
      setFeed('disconnected');
      return undefined;
    }

    fetchSnapshotRef
      .current(token)
      .then((result) => {
        if (cancelled) return;
        if (result.status === 200 && result.payload !== null) {
          setData({ kind: 'ready', snapshot: result.payload });
          // Open the live feed only after an authorized snapshot succeeded.
          socket = createSocketRef.current(streamUrlRef.current(token));
          if (socket === null) {
            setFeed('disconnected');
            return;
          }
          const opened = socket;
          opened.onopen = () => {
            if (!cancelled) setFeed('live');
          };
          opened.onmessage = (event) => {
            if (cancelled) return;
            const snapshot = parseStreamFrame(event.data);
            if (snapshot !== null) {
              setData({ kind: 'ready', snapshot });
              setFeed('live');
            }
          };
          opened.onclose = () => {
            if (!cancelled) setFeed('disconnected');
          };
          opened.onerror = () => {
            if (!cancelled) setFeed('disconnected');
          };
        } else if (result.status === 401) {
          setData({ kind: 'unauthorized' });
          setFeed('disconnected');
        } else if (result.status === 403) {
          setData({ kind: 'blocked', message: result.message ?? 'Forbidden' });
          setFeed('disconnected');
        } else if (result.status === 200) {
          setData({ kind: 'error', message: 'Respons ringkasan tidak valid.' });
          setFeed('disconnected');
        } else {
          setData({ kind: 'error', message: result.message ?? `HTTP ${result.status}` });
          setFeed('disconnected');
        }
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setData({
          kind: 'error',
          message: error instanceof Error ? error.message : 'Network error',
        });
        setFeed('disconnected');
      });

    return () => {
      cancelled = true;
      if (socket !== null) {
        try {
          socket.close();
        } catch {
          // A socket close failure never prevents cleanup.
        }
      }
    };
  }, [token, generation]);

  return { data, feed, reload };
}
